import type { TabConfig } from '@/components/opportunity/useTabsConfig'
import { CROSSCHAIN_SUPPORTED_NETWORKS } from '@/constants/crosschain'
import type { TransactionsContext } from '@/queries/transactionsContext.query'
import { useWalletBalancesQuery } from '@/queries/walletBalances.query'
import type { OpportunityLoader } from '@/routes/opportunity.$network.$vaultId'
import type { CrosschainOption } from '@/types/crosschainOption'
import type { StepData } from '@/types/transactionsContext'
import { useEffect, useMemo, useState } from 'react'
import { useLoaderData } from 'react-router'
import type { Address } from 'viem'
import { ActionTab, hasProtocolException } from './actionTab'
import { CrosschainActionTab } from './crosschainActionTab'

interface DepositActionContainerProps {
  account: Address | undefined
  stepData: StepData
  tabConfig: TabConfig
  setDisplayFundWallet: (value: boolean) => void
  transactionsContext: TransactionsContext
  setTransactionFinished: ({ txHash }: { txHash: string | undefined; amount: string }) => void
  txState: 'idle' | 'loading' | 'success'
  canShowCrosschain: boolean
}

export const DepositActionContainer = ({
  account,
  stepData,
  tabConfig,
  setDisplayFundWallet,
  transactionsContext,
  setTransactionFinished,
  txState,
}: DepositActionContainerProps) => {
  const { data: walletBalances } = useWalletBalancesQuery({ address: account })
  const { vault } = useLoaderData<OpportunityLoader>()
  const { asset, network } = vault

  const selectedInputKey: string | undefined = tabConfig.form.watch('crosschainInput')
  const setSelectedInput = (input: CrosschainOption) =>
    tabConfig.form.setValue('crosschainInput', `${input.network}:${input.address.toLowerCase()}`)
  const [activeView, setActiveView] = useState<'default' | 'crosschain-selector' | 'crosschain'>('default')

  const crosschainOptions = useMemo<CrosschainOption[]>(() => {
    if (!walletBalances) return []

    return walletBalances
      .filter((balance) => {
        if (balance.symbol !== asset.symbol) return false
        if (balance.balanceNative === 0n) return false
        if (!CROSSCHAIN_SUPPORTED_NETWORKS.includes(balance.network.name)) return false
        return true
      })
      .map((balance) => ({
        address: balance.address as Address,
        network: balance.network.name,
        symbol: balance.symbol,
        balanceNative: balance.balanceNative,
        decimals: balance.decimals,
        balanceUsd: balance.balanceUsd,
      }))
  }, [walletBalances, asset.symbol])

  const selectedInput =
    crosschainOptions.find((input) => `${input.network}:${input.address.toLowerCase()}` === selectedInputKey) ??
    crosschainOptions[0]

  const _hasOtherChainFunds = useMemo(() => {
    return crosschainOptions.some((option) => option.network !== network.name)
  }, [crosschainOptions, network.name])

  const isSelectedSameChain = selectedInput?.network === network.name

  const shouldShowCrosschainFlow = false // canShowCrosschain && !isLoadingBalances && hasOtherChainFunds

  useEffect(() => {
    const isTxActive = txState === 'loading' || txState === 'success'
    if (isTxActive) return

    if (!shouldShowCrosschainFlow) {
      setActiveView('default')
    } else if (isSelectedSameChain) {
      setActiveView('crosschain-selector')
    } else {
      setActiveView('crosschain')
    }
  }, [shouldShowCrosschainFlow, isSelectedSameChain, txState])

  if (hasProtocolException(vault, stepData, transactionsContext) || activeView === 'default') {
    return (
      <ActionTab
        account={account}
        stepData={stepData}
        tabConfig={tabConfig}
        setDisplayFundWallet={setDisplayFundWallet}
        transactionsContext={transactionsContext}
        setTransactionFinished={setTransactionFinished}
        txState={txState}
      />
    )
  }

  if (activeView === 'crosschain-selector') {
    return (
      <ActionTab
        account={account}
        stepData={stepData}
        tabConfig={tabConfig}
        setDisplayFundWallet={setDisplayFundWallet}
        transactionsContext={transactionsContext}
        setTransactionFinished={setTransactionFinished}
        txState={txState}
        crosschainOptions={crosschainOptions}
        selectedInput={selectedInput}
        setSelectedInput={setSelectedInput}
      />
    )
  }

  return (
    <CrosschainActionTab
      account={account}
      stepData={stepData}
      tabConfig={tabConfig}
      transactionsContext={transactionsContext}
      setTransactionFinished={setTransactionFinished}
      txState={txState}
      crosschainOptions={crosschainOptions}
      selectedInput={selectedInput!}
      setSelectedInput={setSelectedInput}
    />
  )
}
